import { useState, useEffect }  from 'react';
import CardSection               from '../sections/CardSection';
import InputField                from '../form/InputField';
import SelectField               from '../form/SelectField';
import { CreditCard }            from 'lucide-react';
import { toast } from 'react-toastify';

export default function VehicleRegistrationSection({
    data,
    ciudadOrganismo,
    onChange,
    errors
}) {
    const ciudadOrganismoOptions = Array.isArray(ciudadOrganismo)
        ? ciudadOrganismo.map(item => ({
            value: item.codigo,
            label: item.descripcion
        }))
        : [];

    // onChange para archivos: guarda el File directamente en formData
    const handleFile = (field, files) => {
        if (files && files[0]) {
            const file = files[0];
            // Solo permitir archivos de imagen
            if (!file.type.startsWith('image/')) {
                toast.error('Archivo no válido. Solo se permiten imágenes.');
                return;
            }
            onChange(field, file);
        }
    };

    // Estados para preview de imágenes
    const [frontalPreview, setFrontalPreview]     = useState(null);
    const [posteriorPreview, setPosteriorPreview] = useState(null);

    // Generar/revocar URL para frontal
    useEffect(() => {
        if (data.fotoMatriculaFrontal instanceof File) {
            const url = URL.createObjectURL(data.fotoMatriculaFrontal);
            setFrontalPreview(url);
            return () => URL.revokeObjectURL(url);
        }
        if (typeof data.fotoMatriculaFrontal === 'string') {
            setFrontalPreview(data.fotoMatriculaFrontal);
            return;
        }
        setFrontalPreview(null);
    }, [data.fotoMatriculaFrontal]);

    // Generar/revocar URL para posterior
    useEffect(() => {
        if (data.fotoMatriculaPosterior instanceof File) {
            const url = URL.createObjectURL(data.fotoMatriculaPosterior);
            setPosteriorPreview(url);
            return () => URL.revokeObjectURL(url);
        }
        if (typeof data.fotoMatriculaPosterior === 'string') {
            setPosteriorPreview(data.fotoMatriculaPosterior);
            return;
        }
        setPosteriorPreview(null);
    }, [data.fotoMatriculaPosterior]);

    // Fecha de hoy en formato YYYY-MM-DD, para usar como máximo
    const today = new Date().toISOString().split('T')[0];

    return (
        <CardSection icon="article" title="Registro y documentación">
            <div className="row">
                <div className="col-md-4">
                    <InputField
                        id="fechaMatricula"
                        label="Fecha de Matrícula"
                        type="date"
                        required
                        value={data.fechaMatricula || ''}
                        onChange={onChange}
                        max={today} 
                        onKeyDown={e => e.preventDefault()}
                        onPaste={e => e.preventDefault()}
                        error={errors.fechaMatricula}
                    />
                </div>
                <div className="col-md-4">
                    <InputField
                        id="numeroMotor"
                        label="N° de Motor"
                        placeholder="Ingrese número"
                        uppercase
                        required
                        value={data.numeroMotor || ''}
                        onChange={onChange}
                        error={errors.numeroMotor}
                    />
                </div>
                <div className="col-md-4">
                    <InputField
                        id="numeroChasis"
                        label="N° de Chasis"
                        placeholder="Ingrese número"
                        uppercase
                        required
                        value={data.numeroChasis || ''}
                        onChange={onChange}
                        error={errors.numeroChasis}
                    />
                </div>
                <div className="col-md-6">
                    <InputField
                        id="licenciaTransito"
                        label="Licencia de Tránsito"
                        placeholder="Ingrese número"
                        required
                        value={data.licenciaTransito || ''}
                        onChange={onChange}
                        error={errors.licenciaTransito}
                    />
                </div>
                <div className="col-md-6">
                    <SelectField
                        id="ciudadOrganismoTransito"
                        label="Ciudad Organismo de Tránsito"
                        options={ciudadOrganismoOptions}
                        required
                        value={data.ciudadOrganismoTransito || ''}
                        onChange={onChange}
                        error={errors.ciudadOrganismoTransito}
                        isSearchable={true}
                    />
                </div>
            </div>

            <hr className="my-4" />
            <h5 className="mb-3 text-center">Fotos de la matrícula del vehículo</h5>
            <div className="row">
                {/* Foto Frontal */}
                <div className="col-md-6 text-center">
                    <div className="mb-2">
                        {!frontalPreview && (
                            <CreditCard size={64} className="mb-2 text-secondary" />
                        )}
                        {frontalPreview ? (
                            <img
                                src={frontalPreview}
                                alt="Frontal"
                                className="img-fluid registro-img-preview"
                                style={{
                                    width: '100%',
                                    maxWidth: '400px',
                                    height: '300px',
                                    maxHeight: '300px',
                                    objectFit: 'contain'
                                }}
                            />
                        ) : (
                            <div className="registro-img-placeholder">Frontal</div>
                        )}
                    </div>
                    <label className="btn btn-secondary">
                        Subir imagen
                        <input
                            type="file"
                            accept="image/gif, image/jpeg, image/jpg, image/png, image/svg"
                            hidden
                            onChange={e =>
                                handleFile('fotoMatriculaFrontal', e.target.files)
                            }
                        />
                    </label>
                    {errors.fotoMatriculaFrontal && (
                        <div className="text-danger mt-1">
                            {errors.fotoMatriculaFrontal}
                        </div>
                    )}
                </div>

                {/* Foto Posterior */}
                <div className="col-md-6 text-center">
                    <div className="mb-2">
                        {!posteriorPreview && (
                            <CreditCard size={64} className="mb-2 text-secondary" />
                        )}
                        {posteriorPreview ? (
                            <img
                                src={posteriorPreview}
                                alt="Posterior"
                                className="img-fluid registro-img-preview"
                                style={{
                                    width: '100%',
                                    maxWidth: '400px',
                                    height: '300px',
                                    maxHeight: '300px',
                                    objectFit: 'contain'
                                }}
                            />
                        ) : (
                            <div className="registro-img-placeholder">Posterior</div>
                        )}
                    </div>
                    <label className="btn btn-secondary">
                        Subir imagen
                        <input
                            type="file"
                            accept="image/gif, image/jpeg, image/jpg, image/png, image/svg"
                            hidden
                            onChange={e =>
                                handleFile('fotoMatriculaPosterior', e.target.files)
                            }
                        />
                    </label>
                    {errors.fotoMatriculaPosterior && (
                        <div className="text-danger mt-1">
                            {errors.fotoMatriculaPosterior}
                        </div>
                    )}
                </div>
            </div>
        </CardSection>
    );
}
